import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reverse } from 'lodash';

import { actions } from '../../SuperUser/Report/redux/actions';
import { graphContainer } from './graphContainers';
import { ColorPalette } from '../DashboardColorPalettes';
import GenericCategoryGraph, { chartTypeEnum } from '../../../components/Graphs/GenericCategoryGraph';
import { getActiveKitsCount, MetricsForTrendPlot } from './MetricTrend';

export const getTrendAllIntervalsMetadata = metric => {
  return {
    id: `TrendAllIntervalsWidget_${metric}`,
    name: `${MetricsForTrendPlot[metric]?.label} Trend (multi-interval)`,
    permission: '',
    defProps: { i: `TrendAllIntervalsWidget_${metric}`, w: 4, h: 4, minW: 2, minH: 4 },
    configuration: {
      chartType: {
        type: 'select',
        name: 'chartType',
        label: 'Chart Type',
        defaultValue: chartTypeEnum.horizontalBar,
        options: [
          { label: 'Bar chart', value: chartTypeEnum.bar },
          { label: 'Horizontal bar chart', value: chartTypeEnum.horizontalBar },
          { label: 'Grid', value: "grid" },
        ],
        canBeSetByDashboard: false,
      },
      rollingPeriod: {
        type: 'select',
        name: 'rollingPeriod',
        label: 'Rolling Period (days + weeks + months)',
        defaultValue: 2,
        options: [
            { label: '2', value: 2 },
            { label: '3', value: 3 },
            { label: '4', value: 4 },
            { label: '5', value: 5 },
            { label: '6', value: 6 },
        ],
        canBeSetByDashboard: false,
      },
    },
  };
};

export const genIntervalName = (plotInterval, index) => {
  switch (plotInterval) {
    case 'days':
      if(index === 0) return `Today`;
      if(index === 1) return `Yesterday`;
      return `${index} days ago`;
    case 'weeks':
      if(index === 0) return `This week`;
      if(index === 1) return `Last week`;
      return `${index} weeks ago`;
    case 'months':
      if(index === 0) return `This month`;
      if(index === 1) return `Last month`;
      return `${index} months ago`;
    default:
      if(index === 0) return `This month`;
      if(index === 1) return `Last month`;
      return `${index} months ago`;
  }
};

export const genSeriesName = (index) => {
    return `${-index}`;
};


export const plotIntervals = {
  daily: 'days',
  weekly: 'weeks',
  monthly: 'months',
};

function MetricTrendAllIntervals(props) {
  const { rollingPeriod, metric, data } = props;
  const { colorPalette, graphType } = props;

  const dataSeries = Array.from({ length: rollingPeriod }, (_, i) => i).map(index =>
  ({
      name: genSeriesName(index),
      color: colorPalette?.interpolate(index === 0 ? 0.53 :  0.73 + index * 0.05),
      selector: item => item[genSeriesName(index)],
  }));
  //reverse(dataSeries);

  const graphData = [];
  Object.values(plotIntervals).forEach(plotInterval => {
    const cat = { plotInterval, values: [] };
    Array.from({ length: rollingPeriod }, (_, i) => {
      const catName = genSeriesName(i);
      cat[catName] = metric === "active_kits" ? getActiveKitsCount(data?.[plotInterval]?.[-i]?.days_with_vitals) : data?.[plotInterval]?.[-i]?.[metric] || 0;
      cat.values.push(cat[catName]);
    });
    graphData.push(cat);
  });

  const calcLabel = (catIndexOrValue, dsIndex, value) => {
    const pn = genIntervalName(Object.values(plotIntervals).includes(catIndexOrValue) ? catIndexOrValue : Object.values(plotIntervals)[catIndexOrValue], dsIndex);
    return {text: `${pn}: ${value}`, lines: [`${pn}:`, `${value}`]};
  };

  const grid = (
    <div className="box2colsWithVl vncols" style={{ gridTemplateColumns: `repeat(${rollingPeriod}, 1fr)` }}>
      {graphData &&
        graphData.map(v => v.values.map((value, index) => (
          <div className="box2colsWithVl-box" key={`${v.plotInterval}-${index}`}>
            <div className="box2colsWithVl-valueBox black">
              {value}
            </div>
            <div className="box2colsWithVl-labelBox">
              {genIntervalName(v.plotInterval, index)}
            </div>
          </div>
        )))}
    </div>
  );

  const graph = (
    <GenericCategoryGraph
      graphData={graphData}
      hasBorder
      forceCondensed
      xAxisTopPosition={false}
      yAxisWidth={graphType === chartTypeEnum.horizontalBar ? 100 : 40}
      yAxisUnit=""
      tooltipTitle={`Number of ${MetricsForTrendPlot[metric]?.label}`}
      dataSeries={dataSeries}
      labelFormatter={(catIndex, dsIndex, value) => calcLabel(catIndex, dsIndex, value)}
      categorySelector={item => item.plotInterval}
      hasReferenceAreas
      graphType={graphType}
      skipCategoryAxis
    />
  );

  return graphContainer(graphType === "grid" ? grid : graph, `Number of ${MetricsForTrendPlot[metric]?.label}`);
}

MetricTrendAllIntervals.propTypes = {
  getPatientsStatistics: PropTypes.func,
  onClick: PropTypes.func,
  colorPalette: PropTypes.any,
  refreshTimestamp: PropTypes.any,
  graphType: PropTypes.string,
  dragging: PropTypes.bool,
};

const mapStateToProps = state => {
  return {
    colorPalette:
      ColorPalette[state.dashboardState.configuration?.allDashboards?.colorPalette] || Object.values(ColorPalette)[0],
  };
};


const mapDispatchToProps = dispatch => ({
  getEnrollmentsMetrics: pageRequest => dispatch(actions.getEnrollmentsMetrics(pageRequest)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MetricTrendAllIntervals);
